// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Trophy, Clock, Play, CheckCircle, Info } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { parseLocalDate } from '@/utils/formatters';

const DEMO_EVENT_ID = 'f83c2769-b4aa-4ba9-9e3e-5e205fd40047';

const DemoScoring: React.FC = () => {
  const { eventId } = useParams();
  const activeEventId = eventId || DEMO_EVENT_ID;
  
  const [event, setEvent] = useState<any>(null);
  const [tournaments, setTournaments] = useState<any[]>([]);
  const [selectedTournament, setSelectedTournament] = useState<any>(null);
  const [matches, setMatches] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch event details
  useEffect(() => {
    const fetchEvent = async () => {
      const { data } = await supabase
        .from('events')
        .select('id, name, start_date, end_date')
        .eq('id', activeEventId)
        .single();
      
      if (data) setEvent(data);
    };
    fetchEvent();
  }, [activeEventId]);

  // Fetch tournaments for this event
  useEffect(() => {
    const fetchTournaments = async () => {
      const { data, error } = await supabase
        .from('tournaments')
        .select(`
          *,
          events (name, start_date, end_date, venue_name)
        `)
        .eq('event_id', activeEventId)
        .order('created_at', { ascending: true });

      if (!error && data) {
        setTournaments(data);
        if (data.length > 0) {
          setSelectedTournament(data[0]);
        }
      }
      setLoading(false);
    };

    fetchTournaments();
  }, [activeEventId]);

  // Fetch matches for selected tournament
  const fetchMatches = async () => {
    if (!selectedTournament) return;

    const { data, error } = await supabase
      .from('tournament_matches')
      .select(`
        *,
        team1:tournament_brackets!tournament_matches_team1_id_fkey(
          id, team_id, seed_number,
          team:coach_teams(id, team_name)
        ),
        team2:tournament_brackets!tournament_matches_team2_id_fkey(
          id, team_id, seed_number,
          team:coach_teams(id, team_name)
        )
      `)
      .eq('tournament_id', selectedTournament.id)
      .order('round_number', { ascending: true })
      .order('match_number', { ascending: true });

    if (!error) {
      const transformedMatches = (data || []).map(m => ({
        ...m,
        team1: m.team1 ? { id: m.team1.id, name: m.team1.team?.team_name || 'TBD' } : null,
        team2: m.team2 ? { id: m.team2.id, name: m.team2.team?.team_name || 'TBD' } : null
      }));
      setMatches(transformedMatches);
    }
  };

  useEffect(() => {
    fetchMatches();
  }, [selectedTournament]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge variant="default" className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" /> Completed</Badge>;
      case 'in_progress':
        return <Badge variant="default" className="bg-blue-500"><Play className="w-3 h-3 mr-1" /> In Progress</Badge>;
      default:
        return <Badge variant="secondary"><Clock className="w-3 h-3 mr-1" /> Scheduled</Badge>;
    }
  };

  const handleScoreEntry = (match: any) => {
    toast.info('Score entry would open here in production mode');
  };

  if (loading) {
    return <div className="text-center py-8 text-muted-foreground">Loading tournaments...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Tournament Scoring Demo</h1>
        <p className="text-gray-600">Enter match scores and view bracket progression</p>
      </div>

      {/* Demo Event Info */}
      {event && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <Info className="h-5 w-5 text-blue-600" />
              <div>
                <p className="font-medium text-blue-900">Demo Event: {event.name}</p>
                <p className="text-sm text-blue-700">
                  {format(parseLocalDate(event.start_date), 'MMM d, yyyy')} - {format(parseLocalDate(event.end_date), 'MMM d, yyyy')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {tournaments.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Trophy className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Tournaments Yet</h3>
              <p className="text-muted-foreground max-w-md">
                No tournaments have been created for this event yet. 
                Create a tournament from the Tournaments tab to start scoring matches.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Tournament Selector */}
          {tournaments.length > 1 && (
            <Card>
              <CardHeader>
                <CardTitle>Select Tournament</CardTitle>
                <CardDescription>Choose which tournament to manage</CardDescription>
              </CardHeader>
              <CardContent>
                <Select 
                  value={selectedTournament?.id} 
                  onValueChange={(id) => setSelectedTournament(tournaments.find(t => t.id === id))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select tournament..." />
                  </SelectTrigger>
                  <SelectContent>
                    {tournaments.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name} - {t.division_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>
          )}

          {selectedTournament && (
            <Card>
              <CardHeader>
                <CardTitle>Match Scoring - {selectedTournament.name}</CardTitle>
                <CardDescription>Click a match to enter or update scores</CardDescription>
              </CardHeader>
              <CardContent>
                {matches.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    No matches generated yet. Generate brackets from the Tournaments tab.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {matches.map((match) => (
                      <div 
                        key={match.id}
                        className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                        onClick={() => handleScoreEntry(match)}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm text-muted-foreground">
                              Round {match.round_number} • Match {match.match_number}
                            </span>
                            {getStatusBadge(match.status)}
                          </div>
                          <div className="flex items-center gap-4">
                            <span className={`font-medium ${match.winner_id === match.team1_id ? 'text-green-600' : ''}`}>
                              {match.team1?.name || 'TBD'}
                              {match.team1_score !== null && <span className="ml-2 font-bold">{match.team1_score}</span>}
                            </span>
                            <span className="text-muted-foreground">vs</span>
                            <span className={`font-medium ${match.winner_id === match.team2_id ? 'text-green-600' : ''}`}>
                              {match.team2?.name || 'TBD'}
                              {match.team2_score !== null && <span className="ml-2 font-bold">{match.team2_score}</span>}
                            </span>
                          </div>
                        </div>
                        <Button variant="outline" size="sm">
                          {match.status === 'completed' ? 'Edit Score' : 'Enter Score'}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default DemoScoring;